/**
 * authArtwork.js
 *
 * Curated, Aluvo-owned artwork shown on the login / signup screens.
 *
 * Every entry is original Aluvo coin art: the Legendary reward coin of a live
 * collection, exported from the coin pipeline (ids are the collection slugs
 * returned by GET /public/collections). Third-party poster art is excluded.
 * To refresh after collection art changes, re-export the coin PNGs and re-run
 * the cwebp conversion into public/auth-artwork/ (640 + 1024 widths).
 *
 * Files live in `public/auth-artwork/` and are served statically, so they are
 * NOT part of the JS bundle. Each artwork ships in two widths so the panel can
 * pick the smallest asset that still looks crisp (`srcSet` + `sizes`).
 *
 * The same ids are used by the mobile app (`apps/mobile/src/constants/authArtwork.js`)
 * so `artwork_id` is comparable across platforms in analytics.
 */

const ARTWORK_BASE_PATH = '/auth-artwork';

function sources(id) {
    return {
        640: `${ARTWORK_BASE_PATH}/${id}-640.webp`,
        1024: `${ARTWORK_BASE_PATH}/${id}-1024.webp`,
    };
}

export const AUTH_ARTWORK = Object.freeze([
    {
        id: 'the-anime-starter-pack',
        type: 'starter_pack_coin',
        title: 'The Gateway Coin',
        collection: 'The Anime Starter Pack',
        tier: 'legendary',
        altText: 'The Gateway Coin, the Legendary coin for The Anime Starter Pack collection',
        accent: '#4c5fe6',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('the-anime-starter-pack'),
    },
    {
        id: 'nolan-mastery',
        type: 'collection_coin',
        title: 'The Architect\'s Blueprint',
        collection: 'Christopher Nolan Mastery',
        tier: 'legendary',
        altText: 'The Architect\'s Blueprint, the Legendary coin for the Christopher Nolan Mastery collection',
        accent: '#d4af37',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('nolan-mastery'),
    },
    {
        id: 'the-culture-classics',
        type: 'collection_coin',
        title: 'The Golden Era Medal',
        collection: 'The Culture Classics',
        tier: 'legendary',
        altText: 'The Golden Era Medal, the Legendary coin for The Culture Classics collection',
        accent: '#d3462a',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('the-culture-classics'),
    },
    {
        id: 'infinity-saga-essentials',
        type: 'collection_coin',
        title: 'The Infinity Gauntlet',
        collection: 'The Infinity Saga Essentials',
        tier: 'legendary',
        altText: 'The Infinity Gauntlet, the Legendary coin for The Infinity Saga Essentials collection',
        accent: '#7c3aed',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('infinity-saga-essentials'),
    },
    {
        id: 'spider-verse-glitch',
        type: 'collection_coin',
        title: 'The Multiversal Anomaly',
        collection: 'The Spider-Verse Glitch Suite',
        tier: 'legendary',
        altText: 'The Multiversal Anomaly, the Legendary coin for The Spider-Verse Glitch Suite collection',
        accent: '#e0338f',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('spider-verse-glitch'),
    },
    {
        id: 'the-shonen-throne',
        type: 'collection_coin',
        title: 'The Hokage Seal',
        collection: 'The Shonen Throne',
        tier: 'legendary',
        altText: 'The Hokage Seal, the Legendary coin for The Shonen Throne collection',
        accent: '#e08a1e',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('the-shonen-throne'),
    },
    {
        id: 'the-crown-jewel',
        type: 'collection_coin',
        title: 'The Prestige TV Throne',
        collection: 'The Crown Jewel',
        tier: 'legendary',
        altText: 'The Prestige TV Throne, the Legendary coin for The Crown Jewel collection',
        accent: '#4fb3d9',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('the-crown-jewel'),
    },
    {
        id: 'the-black-tie-protocol',
        type: 'collection_coin',
        title: 'The 00 Cipher',
        collection: 'The Black Tie Protocol',
        tier: 'legendary',
        altText: 'The 00 Cipher, the Legendary coin for The Black Tie Protocol collection',
        accent: '#1f7a4d',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('the-black-tie-protocol'),
    },
    {
        id: 'the-storyteller-s-studio',
        type: 'collection_coin',
        title: 'The Animator\'s Lamp',
        collection: 'The Storyteller\'s Studio',
        tier: 'legendary',
        altText: 'The Animator\'s Lamp, the Legendary coin for The Storyteller\'s Studio collection',
        accent: '#c68a3a',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('the-storyteller-s-studio'),
    },
    {
        id: 'the-main-character-energy-suite',
        type: 'collection_coin',
        title: 'The It Girl Token',
        collection: 'The Main Character Energy Suite',
        tier: 'legendary',
        altText: 'The It Girl Token, the Legendary coin for The Main Character Energy Suite collection',
        accent: '#e35d9c',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('the-main-character-energy-suite'),
    },
    {
        id: 'tarantino-unleashed',
        type: 'collection_coin',
        title: 'The Briefcase',
        collection: 'Tarantino Unleashed',
        tier: 'legendary',
        altText: 'The Briefcase, the Legendary coin for the Tarantino Unleashed collection',
        accent: '#e0b23c',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('tarantino-unleashed'),
    },
    {
        id: 'panem-legacy',
        type: 'collection_coin',
        title: 'The Mockingjay',
        collection: 'The Panem Legacy',
        tier: 'legendary',
        altText: 'The Mockingjay, the Legendary coin for The Panem Legacy collection',
        accent: '#c9622a',
        focalPoint: { x: 0.5, y: 0.5 },
        sources: sources('panem-legacy'),
    },
]);

/** Milliseconds between artwork transitions on web. Slow and subtle on purpose. */
export const AUTH_ARTWORK_INTERVAL_MS = 9000;

/** Largest rendered width of the artwork in the panel; used for `sizes`. */
export const AUTH_ARTWORK_MAX_RENDER_WIDTH = 560;

export function getAuthArtworkSrc(artwork) {
    return artwork?.sources?.[640] || null;
}

export function getAuthArtworkSrcSet(artwork) {
    if (!artwork?.sources) return undefined;
    return Object.entries(artwork.sources)
        .map(([width, url]) => `${url} ${width}w`)
        .join(', ');
}

/**
 * Pick a starting artwork. Rotates the starting piece between visits so
 * returning users do not always see the same coin, without any randomness
 * that would make tests or SSR snapshots flaky.
 */
export function pickInitialArtworkIndex(count, seed = Date.now()) {
    if (!count) return 0;
    const minutes = Math.floor(Number(seed) / 60000);
    return Math.abs(minutes) % count;
}
